import React, { useEffect, useState, useContext } from "react";
import { Badge, Caption, Stack, Tooltip, Avatar, ResourceItem, TextStyle } from "@shopify/polaris";
import { CancelSmallMinor, SendMajor } from "@shopify/polaris-icons";
import PropTypes from "prop-types";

// import from lib
import { baseHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import Popover from "lib/components/popover/popover";

const InvitationListView = (props) => {
  const {
    item,
    handleCancel,
    handleResend,
    cancelButtonLoading,
    resendButtonLoading,
    selectedButtonIndex,
    activePopover,
    setActivePopover,
  } = props;
  const { CANCEL, RESEND } = constant;
  const { _id, email, storeName, createdAt, emailStatus, status } = item;
  const { cms } = useContext(PrivateContext);
  const [selected, setSelected] = useState("");
  const pageContent = {
    label: {
      status: cms("label.status"),
      emailStatus: cms("label.emailStatus"),
      invitedBy: cms("label.invitedBy"),
      invitedOn: cms("label.invitedOn"),
      cancel: cms("button.cancel"),
      resend: cms("button.resend"),
    },
  };

  const badgeStatus = baseHelper.ucFirst(status);
  const badgeType = baseHelper.getBadgeType(status);

  const emailBadgeStatus = baseHelper.ucFirst(emailStatus);
  const emailBadgeType = baseHelper.getBadgeType(emailStatus);
  const media = <Avatar customer size="medium" name="" />;

  const handleSelectChange = (value, id) => {
    setSelected(value);
    if (value === CANCEL) {
      handleCancel(email, id);
    }
    if (value === RESEND) {
      handleResend(email, id);
    }
  };

  useEffect(() => {
    if (selected && (!resendButtonLoading || !cancelButtonLoading)) {
      setSelected("");
    }
  }, [cancelButtonLoading, resendButtonLoading, selected]);

  const topMargin10 = {
    marginTop: "10px",
  };

  return (
    <ResourceItem
      id={_id}
      media={media}
      accessibilityLabel={`${cms("label.viewDetailsFor")} ${storeName}`}
      persistActions
    >
      <Stack alignment="leading">
        <Stack.Item fill>
          <>
            <TextStyle>
              <div className="ellipsis">{email}</div>
            </TextStyle>
            <Caption>
              <TextStyle>{`${pageContent.label.invitedOn}: ${baseHelper.formatDate(createdAt)}`}</TextStyle>
              <>
                <br />
              </>
              {`${pageContent.label.invitedBy}:`}
              <TextStyle>{` ${storeName}`}</TextStyle>
            </Caption>
          </>
        </Stack.Item>
        <Stack.Item>
          <Popover
            // eslint-disable-next-line no-underscore-dangle
            active={activePopover[_id]}
            // eslint-disable-next-line no-underscore-dangle
            setActive={() => setActivePopover({ [_id]: !activePopover[_id] })}
            loading={_id === selectedButtonIndex && (resendButtonLoading || cancelButtonLoading)}
            disabled={status && status.toLowerCase() === constant.JOINED.toLowerCase()}
            options={[
              {
                content: `${pageContent.label.resend}`,
                icon: SendMajor,
                onAction: () => handleSelectChange(`${pageContent.label.resend}`, _id),
              },
              {
                content: `${pageContent.label.cancel}`,
                destructive: true,
                icon: CancelSmallMinor,
                onAction: () => handleSelectChange(`${pageContent.label.cancel}`, _id),
              },
            ]}
          />
        </Stack.Item>
      </Stack>
      <div style={topMargin10}>
        <Stack>
          <Stack.Item>
            <Tooltip content={pageContent.label.emailStatus} preferredPosition="above">
              <Badge status={emailBadgeType}>
                <span className="text-capitalize" id="emailStatus">
                  {emailBadgeStatus}
                </span>
              </Badge>
            </Tooltip>
          </Stack.Item>
          <Stack.Item>
            <Tooltip content={pageContent.label.status} preferredPosition="above">
              <Badge status={badgeType}>
                <span className="text-capitalize" id="inviteStatus">
                  {badgeStatus}
                </span>
              </Badge>
            </Tooltip>
          </Stack.Item>
        </Stack>
      </div>
    </ResourceItem>
  );
};

InvitationListView.defaultProps = {
  activePopover: false,
  cancelButtonLoading: false,
  handleCancel: () => {},
  handleResend: () => {},
  item: {},
  resendButtonLoading: false,
  selectedButtonIndex: "",
  setActivePopover: () => {},
};

InvitationListView.propTypes = {
  activePopover: PropTypes.bool,
  cancelButtonLoading: PropTypes.bool,
  handleCancel: PropTypes.func,
  handleResend: PropTypes.func,
  item: PropTypes.shape(PropTypes.object),
  resendButtonLoading: PropTypes.bool,
  selectedButtonIndex: PropTypes.number,
  setActivePopover: PropTypes.func,
};

export default InvitationListView;
